/**
 * /admin/ingest/review — per-field disagreement queue (K6 / N3).
 *
 * Lists every PENDING ReviewItem. Each row is a per-field conflict
 * between sources (e.g. Opta says "Cover Drive", vendor says "Off
 * Drive"). Admin picks the winner — that value writes back to
 * CanonicalBall + a gold-weight (3.0) AIObservation feeds the next
 * fine-tune cycle. Skip keeps the auto-pick + marks reviewed.
 *
 * Backend:
 *   GET  /api/admin/v2/ingest/review-items/?status=&field_name=&match_id=
 *   POST /api/admin/v2/ingest/review-items/<id>/resolve/  {resolved_value}
 *   POST /api/admin/v2/ingest/review-items/<id>/skip/
 *   POST /api/admin/v2/ingest/review-items/bulk-skip/      {ids: [...]}
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw, AlertTriangle, CheckCircle2, X, ArrowLeft, Loader2,
  Layers, Filter,
} from "lucide-react";
import {
  listReviewItems, resolveReviewItem, skipReviewItem, bulkSkipReviewItems,
} from "../../api/ingest.admin.api";
import ConfirmDialog from "../../components/common/ConfirmDialog";

const FIELD_OPTIONS = [
  { value: "",                  label: "All fields" },
  { value: "runs_off_bat",      label: "runs_off_bat" },
  { value: "extras_runs",       label: "extras_runs" },
  { value: "extras_type",       label: "extras_type" },
  { value: "wicket_type",       label: "wicket_type" },
  { value: "stroke",            label: "stroke" },
  { value: "foot_movement",     label: "foot_movement" },
  { value: "shot_connection",   label: "shot_connection" },
  { value: "wagon_position",    label: "wagon_position" },
  { value: "delivery_type",     label: "delivery_type" },
  { value: "bowler_plan",       label: "bowler_plan" },
];

const STATUS_OPTIONS = [
  { value: "PENDING",   label: "Pending (actionable)" },
  { value: "RESOLVED",  label: "Resolved" },
  { value: "SKIPPED",   label: "Skipped" },
];

export default function AdminIngestReview() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [fieldFilter, setFieldFilter] = useState("");
  const [matchFilter, setMatchFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busyIds, setBusyIds] = useState(new Set());
  const [toast, setToast] = useState("");
  const [bulkSkipOpen, setBulkSkipOpen] = useState(false);
  const [bulkSkipBusy, setBulkSkipBusy] = useState(false);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, fieldFilter, matchFilter]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await listReviewItems({
        status: statusFilter || undefined,
        field_name: fieldFilter || undefined,
        match_id: matchFilter || undefined,
      });
      setRows(data.results || []);
      setCount(data.count || 0);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  }

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllVisible(checked) {
    if (checked) setSelectedIds(new Set(rows.map((r) => r.id)));
    else setSelectedIds(new Set());
  }

  async function doResolve(item, value) {
    setBusyIds((s) => new Set([...s, item.id]));
    try {
      await resolveReviewItem(item.id, value);
      setToast(`Resolved ${item.field_name} → ${JSON.stringify(value)}`);
      setRows((r) => r.filter((x) => x.id !== item.id));
    } catch (e) {
      setToast(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyIds((s) => {
        const n = new Set(s); n.delete(item.id); return n;
      });
    }
  }

  async function doSkip(item) {
    setBusyIds((s) => new Set([...s, item.id]));
    try {
      await skipReviewItem(item.id);
      setRows((r) => r.filter((x) => x.id !== item.id));
    } catch (e) {
      setToast(`Skip failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyIds((s) => {
        const n = new Set(s); n.delete(item.id); return n;
      });
    }
  }

  async function doBulkSkip() {
    if (!selectedIds.size || bulkSkipBusy) return;
    setBulkSkipBusy(true);
    try {
      const { data } = await bulkSkipReviewItems([...selectedIds]);
      setToast(`Skipped ${data.skipped}.`);
      setRows((r) => r.filter((x) => !selectedIds.has(x.id)));
      setSelectedIds(new Set());
      setBulkSkipOpen(false);
    } catch (e) {
      setToast(`Bulk skip failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBulkSkipBusy(false);
    }
  }

  const allChecked = useMemo(
    () => rows.length > 0 && rows.every((r) => selectedIds.has(r.id)),
    [rows, selectedIds],
  );

  return (
    <div className="p-6 space-y-4">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Review queue</h1>
          <p className="text-sm text-text-secondary mt-1">
            {count.toLocaleString("en-IN")} item(s).{" "}
            Pick the correct value per field — your choice becomes canonical truth
            + a gold-weight training row (training_weight 3.0).
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <button
              onClick={() => setBulkSkipOpen(true)}
              className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5"
            >
              <Layers className="w-4 h-4" /> Skip {selectedIds.size} selected
            </button>
          )}
          <button
            onClick={load}
            className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      <div className="flex items-center gap-3 flex-wrap text-sm">
        <Filter className="w-4 h-4 text-text-secondary" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 rounded-md border border-border-default bg-bg-primary"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={fieldFilter}
          onChange={(e) => setFieldFilter(e.target.value)}
          className="px-3 py-1.5 rounded-md border border-border-default bg-bg-primary"
        >
          {FIELD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <input
          value={matchFilter}
          onChange={(e) => setMatchFilter(e.target.value)}
          placeholder="Filter by match UUID"
          className="px-3 py-1.5 rounded-md border border-border-default bg-bg-primary w-72 font-mono text-xs"
        />
      </div>

      {toast && (
        <div
          role="status"
          className="rounded-md border border-blue-500/40 bg-blue-500/10 text-blue-300 px-4 py-2 text-sm flex items-center justify-between"
        >
          <span><CheckCircle2 className="w-4 h-4 inline-block mr-1.5" />{toast}</span>
          <button
            onClick={() => setToast("")}
            aria-label="Dismiss"
            className="text-blue-300 hover:text-blue-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading && rows.length === 0 && (
        <div className="text-center text-text-secondary py-12">
          <Loader2 className="w-5 h-5 inline-block mr-2 animate-spin" /> Loading…
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="text-center text-text-secondary py-12 rounded-md border border-border-default bg-bg-primary">
          {statusFilter === "PENDING"
            ? "🎉 No pending review items. The queue is clear."
            : `No ${statusFilter.toLowerCase()} items match the filters.`}
        </div>
      )}

      <div className="space-y-3">
        {rows.length > 0 && (
          <label className="flex items-center gap-2 text-xs text-text-secondary px-1">
            <input
              type="checkbox"
              checked={allChecked}
              onChange={(e) => selectAllVisible(e.target.checked)}
            />
            Select all visible ({rows.length})
          </label>
        )}
        {rows.map((item) => (
          <ReviewItemCard
            key={item.id}
            item={item}
            busy={busyIds.has(item.id)}
            selected={selectedIds.has(item.id)}
            onToggleSelect={() => toggleSelect(item.id)}
            onResolve={(v) => doResolve(item, v)}
            onSkip={() => doSkip(item)}
          />
        ))}
      </div>

      <ConfirmDialog
        open={bulkSkipOpen}
        title={`Skip ${selectedIds.size} review item(s)?`}
        body={
          <>
            Auto-picked values stand for these conflicts. No <code>AIObservation</code>{" "}
            rows are written, so the next fine-tune cycle won't learn from them.
          </>
        }
        confirmLabel={`Skip ${selectedIds.size}`}
        tone="danger"
        busy={bulkSkipBusy}
        onConfirm={doBulkSkip}
        onCancel={() => setBulkSkipOpen(false)}
      />
    </div>
  );
}

function ReviewItemCard({ item, busy, selected, onToggleSelect, onResolve, onSkip }) {
  const [picked, setPicked] = useState(() => {
    // Default selection = the reconciler's auto_picked_value if present;
    // otherwise first option.
    if (item.auto_picked_value !== null && item.auto_picked_value !== undefined) {
      return JSON.stringify(item.auto_picked_value);
    }
    if (item.options && item.options.length > 0) {
      return JSON.stringify(item.options[0].value);
    }
    return "";
  });

  function handleResolve() {
    if (!picked) return;
    let value;
    try { value = JSON.parse(picked); } catch { value = picked; }
    onResolve(value);
  }

  const isPending = item.status === "PENDING";

  return (
    <div className="rounded-md border border-border-default bg-bg-primary p-4">
      <div className="flex items-start gap-3">
        {isPending && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            className="mt-1"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 font-mono">
              {item.field_name}
            </span>
            <span className="text-text-secondary">·</span>
            <span className="text-text-secondary">
              {item.match_name || "(unnamed match)"} · over {item.over_ball}
            </span>
            {item.status !== "PENDING" && (
              <span className={`text-xs px-2 py-0.5 rounded ${item.status === "RESOLVED" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                {item.status}
              </span>
            )}
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(item.options || []).map((opt, i) => {
              const optJson = JSON.stringify(opt.value);
              const isAuto = JSON.stringify(item.auto_picked_value) === optJson;
              return (
                <label
                  key={i}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${
                    picked === optJson
                      ? "border-button-primary bg-blue-50"
                      : "border-border-default hover:bg-bg-secondary"
                  } ${!isPending ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <input
                    type="radio"
                    name={`pick-${item.id}`}
                    value={optJson}
                    checked={picked === optJson}
                    onChange={() => isPending && setPicked(optJson)}
                    disabled={!isPending}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wider text-text-secondary flex items-center gap-1">
                      <code>{opt.source}</code>
                      {isAuto && (
                        <span className="px-1.5 py-0.5 rounded bg-button-primary text-white text-[10px]">
                          AUTO-PICK
                        </span>
                      )}
                    </div>
                    <div className="font-medium truncate" title={String(opt.value)}>
                      {String(opt.value) || "(empty)"}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          {isPending && (
            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                onClick={onSkip}
                disabled={busy}
                className="px-3 py-1.5 text-sm rounded-md border border-border-default hover:bg-bg-secondary disabled:opacity-50"
              >
                Skip (keep auto-pick)
              </button>
              <button
                onClick={handleResolve}
                disabled={busy || !picked}
                className="px-3 py-1.5 text-sm rounded-md bg-button-primary text-white hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50"
              >
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Resolve with selected
              </button>
            </div>
          )}

          {item.status === "RESOLVED" && (
            <div className="mt-3 text-xs text-text-secondary">
              Resolved to <strong>{JSON.stringify(item.resolved_value)}</strong>{" "}
              · {item.resolved_at && new Date(item.resolved_at).toLocaleString("en-IN")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
